import { useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { usePlanetStore } from '../store/planetStore';
import { usePlayerStore, selectEffectiveTier } from '../store/playerStore';
import { useUniverseStore } from '../store/universeStore';
import { useNow } from '../hooks/useNow';
import { usePlanetResource } from '../hooks/usePlanetResource';
import {
    queueShipUpgrade,
    cancelShipUpgrade,
    rushShipUpgrade,
    type ShipUpgradeActionOptions,
} from '../services/shipUpgrade.service';
import ShipUpgradeCard from '../components/ShipUpgradeCard';
import UnlockSlotsButton from '../components/UnlockSlotsButton';
import { hasHigherQueueTier } from '../utils/queueUpsell';
import PageLoadingState from '../components/PageLoadingState';
import { linkButtonClasses } from '../components/ui/buttonStyles';
import {
    BUILDING_NAMES,
    SHIP_UPGRADE_QUEUE_LIMITS,
    UPGRADABLE_SHIP_IDS,
    getShipDefinition,
    getShipUpgradeQueueLimit,
    type PlayerRace,
    type ScienceId,
    type ShipId,
    type TickCheckpointEventOf,
} from '@kaeliux/shared';
import { extractBuildingLevels } from '@kaeliux/game-engine';

function StarportPageContent({ planetId, race }: { planetId: string; race: PlayerRace }) {
    const { state: { planet, isLoading: isPlanetLoading } } = usePlanetResource();
    const {
        sciences,
        shipUpgrades,
        shipUpgradeQueue,
        error: loadError,
    } = usePlayerStore();
    const effectiveTier = usePlayerStore(selectEffectiveTier);
    const darkMatter = usePlayerStore((s) => s.premium?.darkMatter ?? 0);
    const { resourceMultiplier, speedMultiplier, sunAges } = useUniverseStore();
    const now = useNow();

    const options: ShipUpgradeActionOptions = useMemo(
        () => ({ resourceMultiplier, sunAges, speedMultiplier }),
        [resourceMultiplier, sunAges, speedMultiplier]
    );

    const scienceLevels = useMemo(
        () => (sciences ?? {}) as Partial<Record<ScienceId, number>>,
        [sciences]
    );

    const shipUpgradeLevels = useMemo(
        () => (shipUpgrades ?? {}) as Partial<Record<ShipId, number>>,
        [shipUpgrades]
    );

    const queue = useMemo(
        () => shipUpgradeQueue.filter(
            (e): e is TickCheckpointEventOf<'shipUpgrade'> => e.eventType === 'shipUpgrade' && !e.cancelled
        ),
        [shipUpgradeQueue]
    );

    const buildingLevels = useMemo(
        () => (planet ? extractBuildingLevels(planet.buildings) : ({} as ReturnType<typeof extractBuildingLevels>)),
        [planet]
    );

    // Effective tier (base tier + any unexpired boost) from usePlayerStore's
    // premium state — see selectEffectiveTier in playerStore.ts.
    const queueLimit = getShipUpgradeQueueLimit(effectiveTier);
    const queueFull = queue.length >= queueLimit;
    const techCenterLevel = planet?.buildings.techCenter?.level ?? 0;

    const resources = useMemo(() => {
        if (!planet) return { metal: 0, minerals: 0, gas: 0, energy: 0, ozone: 0 };
        return {
            metal: planet.resources.metal.current,
            minerals: planet.resources.minerals.current,
            gas: planet.resources.gas.current,
            energy: planet.resources.energy.rate - (planet.resources.energy.consumed ?? 0),
            ozone: planet.state.ozone.current,
        };
    }, [planet]);

    if (isPlanetLoading || !planet || sciences === null || shipUpgrades === null) {
        return (
            <div className="min-h-screen bg-space-900 flex items-center justify-center">
                <PageLoadingState message={loadError ?? `Loading ${BUILDING_NAMES.techCenter}…`} />
            </div>
        );
    }

    if (techCenterLevel === 0) {
        return <Navigate to="/zone/military?highlight=techCenter" replace />;
    }

    return (
        <div className="bg-transparent flex flex-col">
            <main className="flex-1 w-full max-w-7xl mx-auto px-4 py-4 flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <h1 className="text-heading font-bold text-content-primary">{BUILDING_NAMES.techCenter}</h1>
                    <div className="flex items-center gap-3">
                        <span className="text-content-muted text-body">
                            Queue: {queue.length} / {queueLimit}
                        </span>
                        {queueFull && hasHigherQueueTier(SHIP_UPGRADE_QUEUE_LIMITS, effectiveTier) && <UnlockSlotsButton />}
                        <Link to="/planet" className={linkButtonClasses('secondary', 'sm')}>← Back to Planet</Link>
                    </div>
                </div>

                <div className="flex flex-col gap-3">
                    {UPGRADABLE_SHIP_IDS.map((shipId) => {
                        const def = getShipDefinition(race, shipId);
                        return (
                            <ShipUpgradeCard
                                key={shipId}
                                def={def}
                                race={race}
                                resources={resources}
                                techCenterLevel={techCenterLevel}
                                speedMultiplier={speedMultiplier}
                                scienceLevels={scienceLevels}
                                buildingLevels={buildingLevels}
                                currentLevel={shipUpgradeLevels[shipId] ?? 0}
                                queuedEvents={queue.filter((e) => e.eventDetails.shipType === shipId)}
                                queueFull={queueFull}
                                now={now}
                                onUpgrade={async () => {
                                    const result = await queueShipUpgrade(planetId, def.id, options);
                                    usePlanetStore.setState({ currentPlanet: result.planet });
                                    usePlayerStore.setState({ shipUpgrades: result.player.shipUpgrades, shipUpgradeQueue: result.player.tickCheckpoints });
                                }}
                                canCancel={() => true}
                                onCancel={async (event) => {
                                    const result = await cancelShipUpgrade(planetId, event.eventId, options);
                                    usePlanetStore.setState({ currentPlanet: result.planet });
                                    usePlayerStore.setState({ shipUpgrades: result.player.shipUpgrades, shipUpgradeQueue: result.player.tickCheckpoints });
                                }}
                                onRush={async (event) => {
                                    const result = await rushShipUpgrade(planetId, event.eventId, options);
                                    usePlanetStore.setState({ currentPlanet: result.planet });
                                    usePlayerStore.setState({
                                        shipUpgrades: result.player.shipUpgrades,
                                        shipUpgradeQueue: result.player.tickCheckpoints,
                                        ...(result.premium ? { premium: result.premium } : {}),
                                    });
                                }}
                                darkMatter={darkMatter}
                            />
                        );
                    })}
                </div>
            </main>
        </div>
    );
}

export default function StarportPage() {
    const { profile } = useAuthStore();
    const activePlanetId = usePlanetStore((s) => s.activePlanetId);

    const planetId = activePlanetId ?? profile?.mainPlanetId ?? '';

    if (!profile || !planetId || !profile.race) {
        return (
            <div className="min-h-screen bg-space-900 flex items-center justify-center">
                <PageLoadingState message="Loading…" />
            </div>
        );
    }

    return (
        <StarportPageContent planetId={planetId} race={profile.race} />
    );
}
